import { useEffect } from 'react';
import Footer from '../../home/components/Footer';

export default function RoomAdditionsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://americandreamremodelinc.com';

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Room Additions',
      description: 'Custom room additions in Southeast Michigan — bedroom additions, sunrooms, kitchen expansions, home offices, second stories, and in-law suites by American Dream Remodeling INC.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'American Dream Remodeling INC',
        url: siteUrl,
        telephone: '+1-586-443-0294',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Detroit',
          addressRegion: 'MI',
          addressCountry: 'US',
        },
      },
      areaServed: [
        { '@type': 'County', name: 'Macomb County' },
        { '@type': 'County', name: 'Oakland County' },
        { '@type': 'County', name: 'Wayne County' },
        { '@type': 'County', name: 'Washtenaw County' },
      ],
      url: `${siteUrl}/services/room-additions`,
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
        { '@type': 'ListItem', position: 3, name: 'Room Additions', item: `${siteUrl}/services/room-additions` },
      ],
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much does a room addition cost in Southeast Michigan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Room addition costs depend on the size, type, and complexity of the project. American Dream Remodeling INC provides free, no-obligation estimates for all room addition projects across Macomb, Oakland, Wayne, and Washtenaw County.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does a room addition take to build?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most room additions take 6–12 weeks depending on size and complexity. Second-story additions or projects requiring significant structural work may take longer. We provide a full timeline during your free consultation.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you handle permits for room additions in Michigan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, American Dream Remodeling INC manages all permits and approvals required for room additions throughout Southeast Michigan, including building, electrical, and plumbing permits.',
          },
        },
        {
          '@type': 'Question',
          name: 'Will the room addition match the existing style of my home?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. We design every room addition to seamlessly integrate with your home\'s existing architecture, materials, and style so the new space looks like it was always part of the original structure.',
          },
        },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Room Additions in Southeast Michigan | American Dream Remodeling INC',
      description: 'Custom room additions in Southeast Michigan — bedroom additions, sunrooms, kitchen expansions, home offices, second stories, and in-law suites by American Dream Remodeling INC.',
      url: `${siteUrl}/services/room-additions`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
          { '@type': 'ListItem', position: 3, name: 'Room Additions', item: `${siteUrl}/services/room-additions` },
        ],
      },
    };

    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    injectSchema('schema-room-additions-service', serviceSchema);
    injectSchema('schema-room-additions-breadcrumb', breadcrumb);
    injectSchema('schema-room-additions-faq', faqSchema);
    injectSchema('schema-room-additions-webpage', webPageSchema);

    return () => {
      ['schema-room-additions-service', 'schema-room-additions-breadcrumb', 'schema-room-additions-faq', 'schema-room-additions-webpage'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const scrollToEstimate = () => {
    sessionStorage.setItem('scrollToEstimate', 'true');
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c2c9872c4b1b6bdbffac73bc1ec04ab5.jpg"
            alt="Room Addition Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Room Additions</h1>
          <p className="text-xl md:text-2xl mb-8">Expand your living space with seamlessly integrated room additions that match your home's existing architecture.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Free Estimate
          </button>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Expand Your Home</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                When you need more space but love your location, a room addition is the perfect solution. Whether you need an extra bedroom, expanded kitchen, sunroom, or home office, we create additions that blend seamlessly with your existing home.
              </p>
              <p className="text-gray-600 leading-relaxed">
                American Dream Remodeling INC handles every aspect of your room addition project, from architectural design and permits to foundation work and final finishes, ensuring your new space looks like it was always part of your home.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=spacious%20sunroom%20addition%20with%20large%20windows%20natural%20light%20comfortable%20furniture%20hardwood%20floors%20clean%20simple%20background%20professional%20interior%20photography&width=800&height=600&seq=addition-detail-001&orientation=landscape"
                alt="Sunroom Addition"
                className="w-full h-full object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>

          {/* What We Offer */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-hotel-bed-line', title: 'Bedroom Additions', desc: 'Extra bedrooms and master suites' },
                { icon: 'ri-sun-line', title: 'Sunrooms', desc: 'Bright, comfortable year-round spaces' },
                { icon: 'ri-restaurant-line', title: 'Kitchen Expansions', desc: 'More space for cooking and entertaining' },
                { icon: 'ri-home-office-line', title: 'Home Offices', desc: 'Dedicated professional workspace' },
                { icon: 'ri-building-2-line', title: 'Second Stories', desc: 'Vertical expansion solutions' },
                { icon: 'ri-home-heart-line', title: 'In-Law Suites', desc: 'Private living spaces for family' },
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500 text-white rounded-lg mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Process */}
          <div className="bg-gradient-to-br from-gray-50 to-white p-12 rounded-2xl border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Planning', desc: 'Design and architectural planning' },
                { step: '2', title: 'Permits', desc: 'Handle all necessary approvals' },
                { step: '3', title: 'Construction', desc: 'Expert building and integration' },
                { step: '4', title: 'Completion', desc: 'Your expanded home is ready' },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-full text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Expand Your Home?</h2>
          <p className="text-xl text-white/90 mb-8">Get a free, no-obligation estimate for your room addition project in Southeast Michigan.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-white text-teal-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Your Free Estimate
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}